'use client'

import { useEffect, useRef } from 'react'

import { stegaClean } from 'next-sanity'

import { ARTICLE_BLOCK_SPACING } from '@/app/utils/articleBlock'
import { cn } from '@/app/utils/style'
import type { DatawrapperEmbed as DatawrapperEmbedType } from '@/sanity.types'
import { parseDatawrapperUrl } from '@/utils/studio/embedPatterns'

const DEFAULT_HEIGHT = 400

interface DatawrapperHeightMessage {
  'datawrapper-height'?: Record<string, number>
}

interface DatawrapperEmbedProps {
  value: DatawrapperEmbedType
  className?: string
}

export function DatawrapperEmbed({ value, className }: DatawrapperEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const url = stegaClean(value.url)
  const chart = parseDatawrapperUrl(url)

  useEffect(() => {
    if (!chart) return
    const handleMessage = (event: MessageEvent<DatawrapperHeightMessage | undefined>) => {
      const iframe = iframeRef.current
      if (!iframe || event.source !== iframe.contentWindow) return
      const height = event.data?.['datawrapper-height']?.[chart.chartId]
      if (typeof height === 'number' && height > 0) iframe.style.height = `${height}px`
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [chart])

  if (!chart) return null

  return (
    <div className={cn(ARTICLE_BLOCK_SPACING, className)}>
      <iframe
        ref={iframeRef}
        id={`datawrapper-chart-${chart.chartId}`}
        src={url}
        title={stegaClean(value.title)}
        loading="lazy"
        scrolling="no"
        className="w-full border-0"
        style={{ height: value.height ?? DEFAULT_HEIGHT }}
      />
    </div>
  )
}
